import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  ArrowBackIosNewOutlined,
  ArrowForwardIosOutlined,
  BrightnessMediumOutlined,
  CropOutlined,
  OpacityOutlined,
  RotateRightOutlined,
  TonalityOutlined,
  HelpOutline,
} from '@mui/icons-material';
import {
  MediaCategory,
  useListingStore,
  useSyncedMediaResource,
} from '@brightmls/nextgen.lae.flc';
import dayjs from 'dayjs';
import { ImageEditDialogProps } from './types';
import { useImageEditDialog } from './hooks';
import { EditButtonsActionBar } from './component/EditButtonsActionBar';
import { RotateControls } from './component/RotateControls';
import { CropControls } from './component/CropControls';
import { BrightnessControls } from './component/BrightnessControls';
import { ContrastControls } from './component/ContrastControls';
import { SaturationControls } from './component/SaturationControls';
import { useModal } from '../../../../contexts/ModalContext';
import {
  CAROUSEL_VIEWPORT_WIDTH,
  CAROUSEL_HEIGHT,
  CAROUSEL_THUMBNAIL_HEIGHT,
  CAROUSEL_ITEM_TOTAL_WIDTH,
  CAROUSEL_WINDOW_SIZE,
} from './constants';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../flags';

export const ImageEditDialog = (props: ImageEditDialogProps) => {
  const { hasFeature } = useFeatureFlags();
  const { showModal } = useModal();
  const { onClose, open, onSave, selectedMediaRecord } = props;

  // Get all media from the listing
  const { displayItems: photoMedia } = useSyncedMediaResource(
    MediaCategory.Photo,
  );

  const showRotateControls = hasFeature(FeatureFlags.NGW_EDIT_IMAGE_ROTATE);
  const showCropControls = hasFeature(FeatureFlags.NGW_EDIT_IMAGE_CROP);

  const {
    // State
    selectedMobileInput,
    setSelectedMobileInput,
    current,
    setCurrent,
    editStates,
    currentPhoto,

    hasEdits,

    // Crop states
    isCropping,
    isSquareCropMode,
    isDraggingSquare,
    isFourThreeCropMode,
    isDraggingFourThree,

    // Carousel states
    isCarouselDragging,
    carouselDragOffset,

    // Refs
    canvasRef,
    imageRef,
    carouselRef,

    // Actions
    updateCurrentEdit,
    handlePrev,
    handleNext,
    handleReset,

    // Crop actions
    startManualCrop,
    startSquareCrop,
    startFourThreeCrop,
    applyCrop,
    applySquareCrop,
    applyFourThreeModeCrop,
    cancelSquareCrop,
    cancelFourThreeCrop,
    cancelManualCrop,

    // Event handlers
    handleImageLoad,
    handleStartCrop,
    handleCropMove,
    handleEndCrop,
    handleSquareDragStart,
    handleSquareDragMove,
    handleSquareDragEnd,
    handleFourThreeDragStart,
    handleFourThreeDragMove,
    handleFourThreeDragEnd,

    // Carousel handlers
    handleCarouselDragStart,
    handleCarouselDragMove,
    handleCarouselDragEnd,
    getVisibleCarouselImagesForState,

    // Processing
    getAllProcessedImages,
  } = useImageEditDialog({
    photoMedia,
    selectedMediaRecord,
  });

  const showResetConfirmation = () => {
    showModal({
      title: 'Reset',
      content: (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <HelpOutline color="action" sx={{ mt: 0.5 }} />
          <Typography
            variant="body1"
            sx={{ color: 'text.primary', lineHeight: 1.5 }}
          >
            This will undo all unsaved changes to this photo. Are you sure you
            want to do this?
          </Typography>
        </Box>
      ),
      confirmText: 'YES',
      cancelText: 'NO',
      onConfirm: handleReset,
    });
  };

  const handleClose = () => {
    onClose(open ? 'true' : 'false');
  };

  const onDragStart = useCallback(
    (
      ev:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (isSquareCropMode) return handleSquareDragStart(ev);
      if (isFourThreeCropMode) return handleFourThreeDragStart(ev);
      if (isCropping) return handleStartCrop(ev);
      return undefined;
    },
    [
      isSquareCropMode,
      isFourThreeCropMode,
      isCropping,
      handleSquareDragStart,
      handleFourThreeDragStart,
      handleStartCrop,
    ],
  );

  const onDragEnd = useCallback(
    (
      ev:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (isSquareCropMode) return handleSquareDragEnd();
      if (isFourThreeCropMode) return handleFourThreeDragEnd();
      if (isCropping) return handleEndCrop(ev);
      return undefined;
    },
    [
      isSquareCropMode,
      isFourThreeCropMode,
      isCropping,
      handleSquareDragEnd,
      handleFourThreeDragEnd,
      handleEndCrop,
    ],
  );

  const onDragMove = useCallback(
    (
      ev:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>,
    ) => {
      if (isSquareCropMode) return handleSquareDragMove(ev);
      if (isFourThreeCropMode) return handleFourThreeDragMove(ev);
      if (isCropping) return handleCropMove(ev);
      return undefined;
    },
    [
      isSquareCropMode,
      isFourThreeCropMode,
      isCropping,
      handleSquareDragMove,
      handleFourThreeDragMove,
      handleCropMove,
    ],
  );

  const mousePointer = useMemo(() => {
    if (isSquareCropMode || isFourThreeCropMode) {
      return isDraggingSquare || isDraggingFourThree ? 'grabbing' : 'grab';
    }

    return isCropping ? 'crosshair' : 'default';
  }, [
    isCropping,
    isDraggingFourThree,
    isDraggingSquare,
    isFourThreeCropMode,
    isSquareCropMode,
  ]);

  const carouselMinWidth = useMemo(() => {
    if (photoMedia.length > CAROUSEL_WINDOW_SIZE) return 1;
    // 2 is from buttons (prev, next)
    return (photoMedia.length + 2) * CAROUSEL_ITEM_TOTAL_WIDTH;
  }, [photoMedia]);

  // Prevent rendering if currentPhoto is undefined (e.g., during upload)
  if (!currentPhoto || !currentPhoto.MEDIA_URL) {
    return null;
  }

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      fullScreen
      keepMounted
      disableEnforceFocus
      aria-labelledby="image-edit-dialog-title"
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          backgroundColor: 'black',
          color: 'common.white',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          p: 2,
        }}
      >
        <IconButton
          edge="start"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
        >
          <ArrowBack />
          <Typography sx={{ pl: 2 }}>
            {photoMedia.length} / 150 Photos
          </Typography>
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
          mb: 4,
          bgColor: 'background.default',
          justifyContent: 'space-between',
        }}
      >
        {/* Photo & Carousel */}
        <Box
          sx={{
            flex: { xs: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 360,
            p: { xs: 1, md: 4 },
            gap: 2,
          }}
        >
          {/* Photo */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <IconButton
              sx={{
                m: 1,
                backgroundColor: 'rgba(255,255,255,0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
              }}
              onClick={handlePrev}
            >
              <ArrowBackIosNewOutlined />
            </IconButton>
            <Card
              sx={{
                borderRadius: 2,
                height: '100%',
                width: '100%',
                maxHeight: { xs: '45vh', md: '70vh' },
                display: 'flex',
                overflow: 'hidden',
                position: 'relative',
                mx: 'auto',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#000',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#000',
                }}
              >
                <canvas
                  ref={canvasRef}
                  onMouseDown={onDragStart}
                  onMouseMove={onDragMove}
                  onMouseUp={onDragEnd}
                  onMouseLeave={onDragEnd}
                  onTouchStart={onDragStart}
                  onTouchMove={onDragMove}
                  onTouchEnd={onDragEnd}
                  // role="img"
                  aria-label="Image being edited"
                  tabIndex={-1}
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'contain',
                    cursor: mousePointer,
                  }}
                />
                {/* Hidden image element to load the source */}
                <img
                  ref={imageRef}
                  crossOrigin="anonymous"
                  src={
                    editStates[current]?.sourceOverride ||
                    currentPhoto.MEDIA_URL
                  }
                  alt={currentPhoto.MEDIA_SHORT_DESCRIPTION}
                  onLoad={handleImageLoad}
                  style={{ display: 'none' }}
                />
              </Box>
            </Card>
            <IconButton
              sx={{
                m: 1,
                backgroundColor: 'rgba(255,255,255,0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
              }}
              onClick={handleNext}
            >
              <ArrowForwardIosOutlined />
            </IconButton>
          </Box>
          {/* Carousel */}
          <Box
            ref={carouselRef}
            sx={{
              alignItems: 'center',
              cursor:
                (photoMedia.length <= 1 && 'default') ||
                (isCarouselDragging ? 'grabbing' : 'grab'),
              display: { xs: 'none', md: 'flex' }, // Hide on mobile/tablet, show on desktop
              height: CAROUSEL_HEIGHT, // Fixed height for carousel
              justifyContent: 'center',
              maxWidth: CAROUSEL_VIEWPORT_WIDTH, // Exactly 9 items * 88px (80px width + 8px margins)
              mx: 'auto',
              overflow: 'hidden',
              position: 'relative',
              userSelect: 'none',
              width: 1,
            }}
            onMouseDown={handleCarouselDragStart}
            onMouseMove={handleCarouselDragMove}
            onMouseUp={handleCarouselDragEnd}
            onMouseLeave={handleCarouselDragEnd}
            onTouchStart={handleCarouselDragStart}
            onTouchMove={handleCarouselDragMove}
            onTouchEnd={handleCarouselDragEnd}
          >
            <Box>
              {photoMedia.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    height: CAROUSEL_THUMBNAIL_HEIGHT,
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    maxWidth: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 1,
                      minWidth: carouselMinWidth,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: 1,
                        display: 'flex',
                        transition: isCarouselDragging
                          ? 'none'
                          : 'transform 0.3s ease',
                        transform: `translateX(${carouselDragOffset}px)`,
                        height: CAROUSEL_THUMBNAIL_HEIGHT,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {getVisibleCarouselImagesForState().map((item) => {
                        return (
                          <Box
                            key={item.index}
                            onClick={() => {
                              if (!isCarouselDragging) {
                                setCurrent(item.index);
                                handleReset();
                              }
                            }}
                            sx={{
                              width: 80,
                              height: CAROUSEL_THUMBNAIL_HEIGHT,
                              border:
                                current === item.index
                                  ? '2px solid orange'
                                  : '2px solid #ddd',
                              borderRadius: 1,
                              overflow: 'hidden',
                              cursor:
                                photoMedia.length <= 1 ? 'default' : 'pointer',
                              opacity: current === item.index ? 1 : 0.7,
                              mx: 0.5,
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={item.photo.MEDIA_URL}
                              alt={item.photo.MEDIA_SHORT_DESCRIPTION}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                pointerEvents: 'none', // Prevent image from interfering with drag
                              }}
                              draggable={false}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      background:
                        'linear-gradient(90deg,rgba(0, 0, 0, 1) 33%, rgba(255, 255, 255, 0) 100%)',
                      height: 1,
                      left: 0,
                      position: 'absolute',
                      width: CAROUSEL_ITEM_TOTAL_WIDTH,
                    }}
                  >
                    <IconButton
                      sx={{
                        m: 1,
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                          color: 'rgba(255,255,255,0.9)',
                        },
                      }}
                      disabled={photoMedia.length <= 1}
                      onClick={handlePrev}
                    >
                      <ArrowBackIosNewOutlined />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      background:
                        'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(0, 0, 0, 1) 67%)',
                      height: 1,
                      position: 'absolute',
                      right: 0,
                      textAlign: 'right',
                      width: CAROUSEL_ITEM_TOTAL_WIDTH,
                    }}
                  >
                    <IconButton
                      sx={{
                        m: 1,
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                          color: 'rgba(255,255,255,0.9)',
                        },
                      }}
                      disabled={photoMedia.length <= 1}
                      onClick={handleNext}
                    >
                      <ArrowForwardIosOutlined />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Typography color="white">No photos available</Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Desktop Tools */}
        <Box
          sx={{
            flex: 1,
            display: { xs: 'none', md: 'flex' }, // Hide on mobile/tablet, show on desktop
            flexDirection: 'column',
            justifyContent: 'space-evenly',
            // rowGap: 2,
            bgcolor: 'grey.700',
            borderRadius: 3,
            p: 3,
            minWidth: 240,
            maxWidth: 400,
            m: { xs: 2, md: 2 },
          }}
        >
          <Typography
            variant="h6"
            color="white"
            mb={2}
            id="image-edit-dialog-title"
          >
            Photo Editor
          </Typography>
          {showRotateControls && (
            <RotateControls
              rotation={editStates[current].rotation}
              onRotationChange={(newRotation) =>
                updateCurrentEdit({ rotation: newRotation })
              }
              disabled={isCropping || isSquareCropMode || isFourThreeCropMode}
            />
          )}
          {showCropControls && (
            <CropControls
              currentEditState={editStates[current]}
              isCropping={isCropping}
              isSquareCropMode={isSquareCropMode}
              isFourThreeCropMode={isFourThreeCropMode}
              applyCrop={applyCrop}
              startManualCrop={startManualCrop}
              startSquareCrop={startSquareCrop}
              applySquareCrop={applySquareCrop}
              cancelSquareCrop={cancelSquareCrop}
              startFourThreeCrop={startFourThreeCrop}
              applyFourThreeModeCrop={applyFourThreeModeCrop}
              cancelFourThreeCrop={cancelFourThreeCrop}
              cancelManualCrop={cancelManualCrop}
            />
          )}
          <Box>
            <BrightnessControls
              brightness={editStates[current].brightness}
              onBrightnessChange={(brightness) =>
                updateCurrentEdit({ brightness })
              }
            />
            <ContrastControls
              contrast={editStates[current].contrast}
              onContrastChange={(contrast) => updateCurrentEdit({ contrast })}
            />
            <SaturationControls
              saturation={editStates[current].saturation}
              onSaturationChange={(saturation) =>
                updateCurrentEdit({ saturation })
              }
            />
          </Box>

          <EditButtonsActionBar
            onSave={onSave}
            onGetAllProcessedImages={getAllProcessedImages}
            onReset={showResetConfirmation}
            isResetDisabled={!hasEdits}
            isSaveDisabled={
              isCropping || isSquareCropMode || isFourThreeCropMode
            }
          />

          <Box sx={{ color: 'lightgray', fontSize: 14 }}>
            <div>
              Date Uploaded:{' '}
              {dayjs(currentPhoto.MEDIA_CREATION_TIMESTAMP).format(
                'D MMMM YYYY',
              )}
            </div>
            <div>
              Photo Size: {currentPhoto.MEDIA_IMAGE_WIDTH}px x{' '}
              {currentPhoto.MEDIA_IMAGE_HEIGHT}px
            </div>
          </Box>
        </Box>

        {/* Mobile Tools */}
        <Box
          sx={{
            flex: 1.5,
            display: { xs: 'flex', md: 'none' }, // Show on mobile/tablet, hide on desktop
            flexDirection: 'column',
            justifyContent: 'space-around',
            alignItems: 'center',
            bgcolor: 'grey.700',
            borderRadius: 3,
            px: 3,
            mx: { xs: 2, md: 2 },
          }}
        >
          <Box sx={{ color: 'lightgray', fontSize: 14 }}>
            <div>
              Date Uploaded:{' '}
              {dayjs(currentPhoto.MEDIA_CREATION_TIMESTAMP).format(
                'D MMMM YYYY',
              )}{' '}
              | Photo Size: {currentPhoto.MEDIA_IMAGE_WIDTH}px x{' '}
              {currentPhoto.MEDIA_IMAGE_HEIGHT}px
            </div>
          </Box>
          <Stack direction="row" spacing={2} m={1}>
            {selectedMobileInput === 'rotate' && (
              <RotateControls
                rotation={editStates[current].rotation}
                onRotationChange={(newRotation) =>
                  updateCurrentEdit({ rotation: newRotation })
                }
                disabled={isCropping || isSquareCropMode || isFourThreeCropMode}
              />
            )}
            {selectedMobileInput === 'crop' && (
              <CropControls
                currentEditState={editStates[current]}
                isCropping={isCropping}
                isSquareCropMode={isSquareCropMode}
                isFourThreeCropMode={isFourThreeCropMode}
                applyCrop={applyCrop}
                startManualCrop={startManualCrop}
                startSquareCrop={startSquareCrop}
                applySquareCrop={applySquareCrop}
                cancelSquareCrop={cancelSquareCrop}
                startFourThreeCrop={startFourThreeCrop}
                applyFourThreeModeCrop={applyFourThreeModeCrop}
                cancelFourThreeCrop={cancelFourThreeCrop}
                cancelManualCrop={cancelManualCrop}
              />
            )}
            {selectedMobileInput === 'brightness' && (
              <BrightnessControls
                brightness={editStates[current].brightness}
                onBrightnessChange={(brightness) =>
                  updateCurrentEdit({ brightness })
                }
              />
            )}
            {selectedMobileInput === 'contrast' && (
              <ContrastControls
                contrast={editStates[current].contrast}
                onContrastChange={(contrast) => updateCurrentEdit({ contrast })}
              />
            )}
            {selectedMobileInput === 'saturation' && (
              <SaturationControls
                saturation={editStates[current].saturation}
                onSaturationChange={(saturation) =>
                  updateCurrentEdit({ saturation })
                }
              />
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            mb={2}
            sx={{
              width: { xs: 1, sm: '75%' },
              justifyContent: 'space-around',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button
                variant={
                  selectedMobileInput === 'rotate' ? 'contained' : 'outlined'
                }
                color="primary"
                onClick={() => setSelectedMobileInput('rotate')}
                sx={{ minWidth: 0, px: 1.5 }}
              >
                <RotateRightOutlined />
              </Button>
              <Typography color="white" variant="caption">
                Rotate
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button
                variant={
                  selectedMobileInput === 'crop' ? 'contained' : 'outlined'
                }
                color="primary"
                onClick={() => setSelectedMobileInput('crop')}
                sx={{ minWidth: 0, px: 1.5 }}
              >
                <CropOutlined />
              </Button>
              <Typography color="white" variant="caption">
                Crop
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button
                variant={
                  selectedMobileInput === 'brightness'
                    ? 'contained'
                    : 'outlined'
                }
                color="primary"
                onClick={() => setSelectedMobileInput('brightness')}
                sx={{ minWidth: 0, px: 1.5 }}
              >
                <BrightnessMediumOutlined />
              </Button>
              <Typography color="white" variant="caption">
                Brightness
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button
                variant={
                  selectedMobileInput === 'contrast' ? 'contained' : 'outlined'
                }
                color="primary"
                onClick={() => setSelectedMobileInput('contrast')}
                sx={{ minWidth: 0, px: 1.5 }}
              >
                <TonalityOutlined />
              </Button>
              <Typography color="white" variant="caption">
                Contrast
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button
                variant={
                  selectedMobileInput === 'saturation'
                    ? 'contained'
                    : 'outlined'
                }
                color="primary"
                onClick={() => setSelectedMobileInput('saturation')}
                sx={{ minWidth: 0, px: 1.5 }}
              >
                <OpacityOutlined />
              </Button>
              <Typography color="white" variant="caption">
                Saturation
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2} mb={2} sx={{ width: '100%' }}>
            <EditButtonsActionBar
              onSave={onSave}
              onGetAllProcessedImages={getAllProcessedImages}
              onReset={showResetConfirmation}
              isResetDisabled={!hasEdits}
              isSaveDisabled={
                isCropping || isSquareCropMode || isFourThreeCropMode
              }
            />
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ImageEditDialog;

// Re-export types for convenience
export type {
  ProcessedImageData,
  BatchProcessedImageData,
  ImageEditDialogProps,
} from './types';
